<template>
  <div class="dtp" @focusout="onFocusOut">
    <div class="dtp-pill" :class="{ 'is-custom': !isDefault, 'is-open': isOpen }">
      <button
        ref="trigger"
        type="button"
        class="dtp-trigger"
        aria-haspopup="dialog"
        :aria-expanded="String(isOpen)"
        :aria-label="`Preview date and time: ${dateLabel}, ${rangeLabel}`"
        @click="toggle"
      >
        <i class="pi pi-calendar-clock dtp-trigger__icon" aria-hidden="true"></i>
        <bdi class="dtp-trigger__date">{{ dateLabel }}</bdi>
        <span class="dtp-trigger__divider" aria-hidden="true"></span>
        <span class="dtp-trigger__time">{{ rangeLabel }}</span>
        <i v-if="isDefault" class="pi pi-chevron-down dtp-trigger__chevron" aria-hidden="true"></i>
      </button>
      <button
        v-if="!isDefault"
        type="button"
        class="dtp-pill__clear"
        :title="RESET_LABEL"
        :aria-label="RESET_LABEL"
        @click="resetToDefault"
      >
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <transition name="dtp-pop">
      <div
        v-if="isOpen"
        ref="panel"
        class="dtp-panel"
        role="dialog"
        aria-modal="false"
        :aria-labelledby="ids.title"
        tabindex="-1"
        @keydown.esc.stop.prevent="onEscape"
      >
        <div class="dtp-panel__head">
          <span class="dtp-panel__glyph" aria-hidden="true">
            <i class="pi pi-calendar-clock"></i>
          </span>
          <div class="dtp-panel__titles">
            <h6 :id="ids.title" class="dtp-panel__title">Date &amp; time</h6>
            <p class="dtp-panel__subtitle">Pick the day and the hours the preview shows.</p>
          </div>
        </div>

        <section class="dtp-section" :aria-labelledby="ids.date">
          <div class="dtp-section__head">
            <span :id="ids.date" class="dtp-section__label">Date</span>
            <button
              type="button"
              class="dtp-chip dtp-chip--sm"
              :class="{ 'is-active': draftIsToday }"
              :aria-pressed="String(draftIsToday)"
              @click="setDraftDate(today)"
            >
              Today
            </button>
          </div>
          <div class="dtp-date">
            <button
              type="button"
              class="dtp-date__step"
              title="Previous day"
              aria-label="Previous day"
              @click="stepDraftDate(-1)"
            >
              <i class="pi pi-chevron-left" aria-hidden="true"></i>
            </button>
            <div class="dtp-date__field" :class="{ 'is-open': showHebrewPicker }">
              <span class="dtp-date__text" aria-hidden="true">
                <bdi class="dtp-date__primary">{{ draftDateLabel }}</bdi>
                <span class="dtp-date__secondary">{{ draftDateDetail }}</span>
              </span>
              <i class="pi pi-calendar dtp-date__icon" aria-hidden="true"></i>
              <!-- The control lies invisibly over the whole field, so a click
                   anywhere on it opens the calendar the organization picks in. -->
              <input
                v-if="isGregorianDisplay"
                type="date"
                class="dtp-date__control dtp-date__native"
                aria-label="Date"
                :value="draftDate"
                @input="setDraftDate($event.target.value)"
                @blur="syncNativeDate"
                @keydown.enter.prevent="apply"
              />
              <button
                v-else
                ref="dateButton"
                type="button"
                class="dtp-date__control"
                aria-haspopup="dialog"
                :aria-expanded="String(showHebrewPicker)"
                :aria-label="`Date: ${draftDateLabel}. Choose a Hebrew date`"
                @click="openHebrewPicker"
              ></button>
            </div>
            <button
              type="button"
              class="dtp-date__step"
              title="Next day"
              aria-label="Next day"
              @click="stepDraftDate(1)"
            >
              <i class="pi pi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </section>

        <section class="dtp-section dtp-section--time" :aria-labelledby="ids.time">
          <div class="dtp-section__head">
            <span :id="ids.time" class="dtp-section__label">Time</span>
            <span class="dtp-duration">{{ durationLabel }}</span>
          </div>

          <div class="dtp-presets" role="group" aria-label="Quick ranges">
            <button
              v-for="preset in presets"
              :key="preset.key"
              type="button"
              class="dtp-chip"
              :class="{ 'is-active': isPresetActive(preset) }"
              :aria-pressed="String(isPresetActive(preset))"
              @click="applyPreset(preset)"
            >
              <span v-if="preset.key === 'now'" class="dtp-chip__live" aria-hidden="true"></span>
              {{ preset.label }}
            </button>
          </div>

          <div class="dtp-timeline">
            <div
              ref="track"
              class="dtp-track"
              @pointerdown="onTrackPointerDown"
              @pointermove="onTrackPointerMove"
              @pointerup="endDrag"
              @pointercancel="endDrag"
              @lostpointercapture="endDrag"
            >
              <div class="dtp-track__rail"></div>
              <div
                class="dtp-track__band"
                :style="bandStyle"
                @pointerdown.stop="startDrag('band', $event)"
              ></div>
              <div
                v-if="showNowMarker"
                class="dtp-track__now"
                :style="{ left: percent(nowMinutes) }"
                :title="`Now · ${formatTime(nowMinutes)}`"
              ></div>
              <button
                v-for="edge in EDGES"
                :key="edge"
                type="button"
                class="dtp-handle"
                :class="{ 'is-dragging': dragEdge === edge }"
                :data-edge="edge"
                :style="{ left: percent(edgeValue(edge)) }"
                role="slider"
                :aria-label="edge === 'start' ? 'Start time' : 'End time'"
                :aria-valuemin="edge === 'start' ? 0 : draftStart"
                :aria-valuemax="edge === 'start' ? draftEnd : DAY_MINUTES"
                :aria-valuenow="edgeValue(edge)"
                :aria-valuetext="formatTime(edgeValue(edge))"
                @pointerdown.stop="startDrag(edge, $event)"
                @keydown="onHandleKeydown(edge, $event)"
              >
                <span class="dtp-handle__bubble">{{ formatTime(edgeValue(edge)) }}</span>
              </button>
            </div>
            <div class="dtp-ticks" aria-hidden="true">
              <span
                v-for="tick in TICKS"
                :key="tick.minutes"
                class="dtp-tick"
                :style="{ left: percent(tick.minutes) }"
              >{{ tick.label }}</span>
            </div>
          </div>

          <div class="dtp-fields">
            <label class="dtp-field" :class="{ 'is-invalid': invalidEdge === 'start' }">
              <span class="dtp-field__label">From</span>
              <input
                ref="startInput"
                class="dtp-field__input"
                type="text"
                inputmode="text"
                autocomplete="off"
                spellcheck="false"
                aria-label="Start time"
                :value="texts.start"
                @input="onFieldInput('start', $event)"
                @keydown="onFieldKeydown('start', $event)"
                @blur="commitField('start')"
              />
            </label>
            <i class="pi pi-arrow-right dtp-fields__arrow" aria-hidden="true"></i>
            <label class="dtp-field" :class="{ 'is-invalid': invalidEdge === 'end' }">
              <span class="dtp-field__label">
                To
                <span v-if="draftEnd === DAY_MINUTES" class="dtp-field__tag">next day</span>
              </span>
              <input
                ref="endInput"
                class="dtp-field__input"
                type="text"
                inputmode="text"
                autocomplete="off"
                spellcheck="false"
                aria-label="End time"
                :value="texts.end"
                @input="onFieldInput('end', $event)"
                @keydown="onFieldKeydown('end', $event)"
                @blur="commitField('end')"
              />
            </label>
          </div>
          <p class="dtp-hint" :class="{ 'is-error': invalidEdge }" aria-live="polite">
            <template v-if="invalidEdge">That isn't a time we can read — try 6:30 pm or 18:30.</template>
            <template v-else>Type 6p, 6:30 pm or 18:30 · ↑ ↓ nudge by 15 minutes</template>
          </p>
        </section>

        <div class="dtp-panel__foot">
          <button type="button" class="dtp-link" :title="RESET_LABEL" @click="setDraftToDefault">Reset</button>
          <div class="dtp-panel__actions">
            <button type="button" class="dtp-btn dtp-btn--ghost" @click="close(true)">Cancel</button>
            <button type="button" class="dtp-btn dtp-btn--primary" @click="apply">Apply</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- The Hebrew date modal stays its own dialog, opened over the panel. While
         it is up, nothing done in it — a press, the focus moving in, Esc —
         closes the panel; picking a day only fills the panel's date. -->
    <Teleport to="body">
      <JewishDatePicker
        v-if="showHebrewPicker"
        ref="hebrewPicker"
        :initial-date="draftDate"
        @select-date="onHebrewDateSelected"
        @close-date-picker-modal="closeHebrewPicker"
      />
    </Teleport>
  </div>
</template>

<script>
import JewishDatePicker from '@/components/Jewish_Date_Picker.vue';
import { DAY_MINUTES, parseDateValue, toLocalNoon } from '@/utils/display_preview';
import { formatHebrewDateLabel } from '@/utils/hebrew_month_grid';
import { toHebrewPickerDateInputValue, toWallClockDateInputValue } from '@/utils/wall_clock_date';

// Dragging and the arrow keys move a time in quarter hours; with Shift, by the hour.
const STEP_MINUTES = 15;
const HOUR_MINUTES = 60;

const EDGES = ['start', 'end'];

const TICKS = [
  { minutes: 0, label: '12 AM' },
  { minutes: 6 * 60, label: '6 AM' },
  { minutes: 12 * 60, label: 'Noon' },
  { minutes: 18 * 60, label: '6 PM' },
  { minutes: DAY_MINUTES, label: '12 AM' },
];

// "Now" takes its time from the screen's clock when the panel renders, and its
// day is today.
const PRESETS = [
  { key: 'all', label: 'All day', start: 0, end: DAY_MINUTES },
  { key: 'now', label: 'Now' },
  { key: 'morning', label: 'Morning', start: 6 * 60, end: 12 * 60 },
  { key: 'afternoon', label: 'Afternoon', start: 12 * 60, end: 18 * 60 },
  { key: 'evening', label: 'Evening', start: 18 * 60, end: DAY_MINUTES },
];

const RESET_LABEL = 'Back to today, all day';

// "Oct 21, 2026" and "Wednesday", read off a calendar day at local noon.
const GREGORIAN_DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'long' });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// "6:30 PM". The day's end, 24:00, reads as the midnight it is.
const formatTime = (minutes) => {
  const total = ((Math.round(minutes) % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  const hour = Math.floor(total / 60);
  return `${hour % 12 || 12}:${String(total % 60).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
};

// A time the way people type one — "6", "6p", "6:30 pm", "630", "18:30",
// "noon", "midnight" — in minutes past midnight, or null when it can't be read.
// As an END, midnight is the next one: "6 PM – 12 AM" runs to the day's end.
const parseTimeText = (text, isEnd) => {
  const raw = String(text || '').toLowerCase().replace(/\s+/g, '').replace(/\./g, ':');
  if (!raw) return null;
  if (raw === 'noon') return 12 * 60;
  if (raw === 'midnight') return isEnd ? DAY_MINUTES : 0;

  const match = raw.match(/^(\d{1,4})(?::(\d{2}))?(a|am|p|pm)?$/);
  if (!match) return null;
  const [, digits, minutePart, meridiem] = match;
  if (minutePart != null && digits.length > 2) return null;

  // "630" or "1830": the last two digits are the minutes.
  const compact = minutePart == null && digits.length > 2;
  let hour = Number(compact ? digits.slice(0, -2) : digits);
  const minute = Number(compact ? digits.slice(-2) : minutePart || 0);
  if (minute > 59) return null;

  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem.startsWith('p') ? 12 : 0);
  } else if (hour > 24 || (hour === 24 && minute > 0)) {
    return null;
  }

  const total = hour * 60 + minute;
  return isEnd && total === 0 ? DAY_MINUTES : total;
};

const formatDuration = (minutes) => {
  if (minutes <= 0) return 'One moment';
  if (minutes >= DAY_MINUTES) return '24 hours';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  if (!rest) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return `${hours} h ${rest} min`;
};

let pickerCount = 0;

export default {
  name: 'DateTimeRangePicker',
  components: { JewishDatePicker },
  props: {
    // The day in force, "YYYY-MM-DD".
    date: {
      type: String,
      default: '',
    },
    // Today on the screen's own clock, "YYYY-MM-DD": the day "Today", "Now" and
    // the reset go back to.
    today: {
      type: String,
      default: '',
    },
    // The range in force, in minutes past midnight: 0 to 1440, 1440 being the
    // next midnight.
    start: {
      type: Number,
      default: 0,
    },
    end: {
      type: Number,
      default: DAY_MINUTES,
    },
    // The current time of day on the screen's own clock, in minutes — for the
    // "Now" range and the marker on today's timeline. Null hides both.
    nowMinutes: {
      type: Number,
      default: null,
    },
  },
  emits: ['apply'],
  data() {
    pickerCount += 1;
    const id = `dtp-${pickerCount}`;
    return {
      DAY_MINUTES,
      EDGES,
      TICKS,
      RESET_LABEL,
      ids: { title: `${id}-title`, date: `${id}-date`, time: `${id}-time` },
      isOpen: false,
      // The day and range being shaped in the panel; they only take effect on Apply.
      draftDate: this.date,
      draftStart: this.start,
      draftEnd: this.end,
      texts: { start: '', end: '' },
      invalidEdge: null,
      // 'start' | 'end' | 'band', or 'either' while two handles sit on top of
      // each other and the first move has yet to say which one is going.
      dragEdge: null,
      dragOrigin: null,
      showHebrewPicker: false,
    };
  },
  computed: {
    // Hebrew dates where the organization picks, and reads, its dates in the
    // Hebrew calendar; Gregorian ones otherwise.
    isHebrewLabel() {
      return !this.isGregorianDisplay && this.displayCalendarType === 'hebrew';
    },
    isAllDay() {
      return this.start <= 0 && this.end >= DAY_MINUTES;
    },
    // Today, all day: what the preview opens on, and what the pill clears back to.
    isDefault() {
      return this.date === this.today && this.isAllDay;
    },
    dateLabel() {
      return this.formatDateLabel(this.date) || 'Today';
    },
    rangeLabel() {
      if (this.isAllDay) return 'All day';
      if (this.start === this.end) return `At ${formatTime(this.start)}`;
      return `${formatTime(this.start)} – ${formatTime(this.end)}`;
    },
    draftIsToday() {
      return Boolean(this.today) && this.draftDate === this.today;
    },
    draftDateLabel() {
      return this.formatDateLabel(this.draftDate);
    },
    // The weekday, and the Gregorian date under a Hebrew one.
    draftDateDetail() {
      const parts = parseDateValue(this.draftDate);
      if (!parts) return '';
      const day = toLocalNoon(parts);
      const weekday = WEEKDAY.format(day);
      return this.isHebrewLabel ? `${weekday}, ${GREGORIAN_DATE.format(day)}` : weekday;
    },
    durationLabel() {
      return formatDuration(this.draftEnd - this.draftStart);
    },
    presets() {
      return PRESETS.map((preset) => {
        if (preset.key !== 'now') return preset;
        return Number.isFinite(this.nowMinutes)
          ? { ...preset, start: this.nowMinutes, end: this.nowMinutes }
          : null;
      }).filter(Boolean);
    },
    showNowMarker() {
      return this.draftIsToday && Number.isFinite(this.nowMinutes);
    },
    bandStyle() {
      return {
        left: this.percent(this.draftStart),
        width: this.percent(this.draftEnd - this.draftStart),
      };
    },
  },
  watch: {
    // The organization switched to Gregorian dates: the panel's date field is a
    // native one now, and the Hebrew modal has nothing left to fill.
    isGregorianDisplay(isGregorian) {
      if (isGregorian) this.showHebrewPicker = false;
    },
  },
  beforeUnmount() {
    this.stopListening();
  },
  methods: {
    formatTime,
    percent(minutes) {
      return `${(clamp(minutes, 0, DAY_MINUTES) / DAY_MINUTES) * 100}%`;
    },
    edgeValue(edge) {
      return edge === 'start' ? this.draftStart : this.draftEnd;
    },
    formatDateLabel(value) {
      const parts = parseDateValue(value);
      if (!parts) return '';
      return this.isHebrewLabel ? formatHebrewDateLabel(value) : GREGORIAN_DATE.format(toLocalNoon(parts));
    },
    toggle() {
      if (this.isOpen) this.close(true);
      else this.open();
    },
    open() {
      this.draftDate = parseDateValue(this.date) ? this.date : this.today;
      this.setDraft(this.start, this.end);
      this.isOpen = true;
      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
      this.$nextTick(() => this.$refs.panel?.focus());
    },
    // Closing without Apply drops whatever was being shaped in the panel.
    close(refocus) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.showHebrewPicker = false;
      this.dragEdge = null;
      this.dragOrigin = null;
      this.stopListening();
      if (refocus) this.$nextTick(() => this.$refs.trigger?.focus());
    },
    stopListening() {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    },
    // A press outside closes the panel. The Hebrew date modal is outside it too,
    // but a press there belongs to the modal.
    onDocumentPointerDown(event) {
      if (this.showHebrewPicker || this.$el.contains(event.target)) return;
      this.close(false);
    },
    // Tabbing on out of the panel closes it, as a click elsewhere does — but not
    // the focus going into the Hebrew date modal. A click on something
    // unfocusable inside the panel moves focus nowhere and is not a leave.
    onFocusOut(event) {
      const next = event.relatedTarget;
      if (this.isOpen && !this.showHebrewPicker && next && !this.$el.contains(next)) this.close(false);
    },
    onEscape() {
      if (this.showHebrewPicker) this.closeHebrewPicker();
      else this.close(true);
    },
    apply() {
      // Whatever is still sitting typed in a field counts too.
      for (const edge of EDGES) {
        if (!this.commitField(edge)) {
          this.$refs[`${edge}Input`]?.focus();
          return;
        }
      }
      this.$emit('apply', { date: this.draftDate, start: this.draftStart, end: this.draftEnd });
      this.close(true);
    },
    resetToDefault() {
      this.close(false);
      this.$emit('apply', { date: this.today, start: 0, end: DAY_MINUTES });
      // The clear button leaves with the custom preview; the pill keeps the focus.
      this.$nextTick(() => this.$refs.trigger?.focus());
    },
    setDraftToDefault() {
      this.setDraftDate(this.today);
      this.setDraft(0, DAY_MINUTES);
    },
    /* -------------------------------- date -------------------------------- */
    // Only a whole, real day is taken: a native date input reports every
    // keystroke of one being typed.
    setDraftDate(value) {
      if (parseDateValue(value)) this.draftDate = value;
    },
    stepDraftDate(days) {
      const parts = parseDateValue(this.draftDate);
      if (!parts) return;
      const day = toLocalNoon(parts);
      day.setDate(day.getDate() + days);
      this.setDraftDate(toWallClockDateInputValue(day));
    },
    // A date left half typed goes back to the day in the panel.
    syncNativeDate(event) {
      if (event.target.value !== this.draftDate) event.target.value = this.draftDate;
    },
    openHebrewPicker() {
      this.showHebrewPicker = true;
      // Into the modal, so the keyboard — Tab, Esc — works there, not behind it.
      this.$nextTick(() => this.$refs.hebrewPicker?.$el?.focus?.({ preventScroll: true }));
    },
    onHebrewDateSelected(day) {
      this.setDraftDate(toHebrewPickerDateInputValue(day));
      this.closeHebrewPicker();
    },
    closeHebrewPicker() {
      if (!this.showHebrewPicker) return;
      this.showHebrewPicker = false;
      this.$nextTick(() => this.$refs.dateButton?.focus());
    },
    /* -------------------------------- time -------------------------------- */
    // "Now" is a moment of today, whichever day was in the panel.
    applyPreset(preset) {
      if (preset.key === 'now') this.setDraftDate(this.today);
      this.setDraft(preset.start, preset.end);
    },
    isPresetActive(preset) {
      if (preset.key === 'now' && !this.draftIsToday) return false;
      return this.draftStart === preset.start && this.draftEnd === preset.end;
    },
    setDraft(start, end) {
      this.draftStart = clamp(Math.round(start), 0, DAY_MINUTES);
      this.draftEnd = clamp(Math.round(end), this.draftStart, DAY_MINUTES);
      this.invalidEdge = null;
      this.texts = { start: formatTime(this.draftStart), end: formatTime(this.draftEnd) };
    },
    // Move one end. Crossing the other end carries it along, so a range is never
    // inside out.
    setEdge(edge, minutes) {
      const value = clamp(Math.round(minutes), 0, DAY_MINUTES);
      if (edge === 'start') this.setDraft(value, Math.max(value, this.draftEnd));
      else this.setDraft(Math.min(value, this.draftStart), value);
    },
    onFieldInput(edge, event) {
      this.texts[edge] = event.target.value;
      if (this.invalidEdge === edge) this.invalidEdge = null;
    },
    // Read what was typed into a field. Text that isn't a time keeps the field
    // red and the range as it was; an emptied field goes back to its time.
    commitField(edge) {
      const text = String(this.texts[edge] || '').trim();
      const shown = formatTime(this.edgeValue(edge));
      // Untouched: re-reading "12:00 AM" would turn the day's end back into its start.
      if (text === shown) return true;
      if (!text) {
        this.texts[edge] = shown;
        return true;
      }
      const minutes = parseTimeText(text, edge === 'end');
      if (minutes == null) {
        this.invalidEdge = edge;
        return false;
      }
      this.setEdge(edge, minutes);
      return true;
    },
    onFieldKeydown(edge, event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (this.commitField(edge)) this.apply();
        return;
      }
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      if (!this.commitField(edge)) return;
      const step = event.shiftKey ? HOUR_MINUTES : STEP_MINUTES;
      this.setEdge(edge, this.edgeValue(edge) + (event.key === 'ArrowUp' ? step : -step));
    },
    /* ------------------------------ timeline ------------------------------ */
    onHandleKeydown(edge, event) {
      const current = this.edgeValue(edge);
      const step = event.shiftKey ? HOUR_MINUTES : STEP_MINUTES;
      const targets = {
        ArrowLeft: current - step,
        ArrowDown: current - step,
        ArrowRight: current + step,
        ArrowUp: current + step,
        PageDown: current - HOUR_MINUTES,
        PageUp: current + HOUR_MINUTES,
        Home: edge === 'start' ? 0 : this.draftStart,
        End: edge === 'start' ? this.draftEnd : DAY_MINUTES,
      };
      if (!(event.key in targets)) return;
      event.preventDefault();
      // A handle stops at the other one rather than pushing it along.
      if (edge === 'start') this.setDraft(clamp(targets[event.key], 0, this.draftEnd), this.draftEnd);
      else this.setDraft(this.draftStart, clamp(targets[event.key], this.draftStart, DAY_MINUTES));
    },
    minutesAt(clientX) {
      const rect = this.$refs.track.getBoundingClientRect();
      if (!rect.width) return 0;
      return clamp((clientX - rect.left) / rect.width, 0, 1) * DAY_MINUTES;
    },
    snap(minutes) {
      return Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;
    },
    // A press on the bare track: the nearer handle jumps there and follows the
    // pointer from then on.
    onTrackPointerDown(event) {
      if (event.button !== 0) return;
      const edge = this.minutesAt(event.clientX) <= this.draftStart ? 'start' : 'end';
      this.startDrag(edge, event);
      this.dragTo(event.clientX);
    },
    startDrag(kind, event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.$refs.track.setPointerCapture?.(event.pointerId);
      const fromHandle = event.currentTarget !== this.$refs.track;
      this.dragEdge = kind !== 'band' && fromHandle && this.draftStart === this.draftEnd ? 'either' : kind;
      this.dragOrigin = { pointer: this.minutesAt(event.clientX), start: this.draftStart, end: this.draftEnd };
      if (kind !== 'band') this.$el.querySelector(`.dtp-handle[data-edge="${kind}"]`)?.focus();
    },
    onTrackPointerMove(event) {
      if (this.dragEdge) this.dragTo(event.clientX);
    },
    dragTo(clientX) {
      const pointer = this.minutesAt(clientX);
      if (this.dragEdge === 'band') {
        const { start, end } = this.dragOrigin;
        const length = end - start;
        const nextStart = clamp(start + this.snap(pointer - this.dragOrigin.pointer), 0, DAY_MINUTES - length);
        this.setDraft(nextStart, nextStart + length);
        return;
      }
      const value = this.snap(pointer);
      if (this.dragEdge === 'either') {
        if (value === this.draftStart) return;
        this.dragEdge = value < this.draftStart ? 'start' : 'end';
      }
      if (this.dragEdge === 'start') this.setDraft(Math.min(value, this.draftEnd), this.draftEnd);
      else this.setDraft(this.draftStart, Math.max(value, this.draftStart));
    },
    endDrag(event) {
      if (!this.dragEdge) return;
      if (this.$refs.track?.hasPointerCapture?.(event.pointerId)) {
        this.$refs.track.releasePointerCapture(event.pointerId);
      }
      this.dragEdge = null;
      this.dragOrigin = null;
    },
  },
};
</script>

<style scoped>
.dtp {
  position: relative;
  display: inline-block;
  min-width: 240px;
  max-width: 100%;
  vertical-align: middle;
}

/* ------------------------------- pill --------------------------------- */
.dtp-pill {
  display: flex;
  align-items: center;
  width: 100%;
  height: 43px;
  background: var(--color-bg-canvas);
  border: 1px solid #d8dce5;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}

.dtp-pill:hover {
  border-color: #c7cad4;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.dtp-pill.is-custom {
  border-color: var(--color-primary-border);
  background: var(--color-primary-soft);
}

.dtp-pill.is-open {
  border-color: var(--color-primary-border);
  box-shadow: var(--shadow-focus);
}

.dtp-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0 15px 0 16px;
  border: 0;
  border-radius: inherit;
  background: transparent;
  color: #71717a;
  font: inherit;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
}

.dtp-pill.is-custom .dtp-trigger {
  padding-right: 8px;
  color: var(--color-primary-hover);
}

.dtp-trigger__icon {
  flex-shrink: 0;
  color: #52525b;
  font-size: 0.95rem;
}

.dtp-pill.is-custom .dtp-trigger__icon {
  color: var(--color-primary);
}

.dtp-trigger__date {
  flex: 0 0 auto;
  font-weight: 600;
  white-space: nowrap;
}

.dtp-trigger__divider {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  background: #d8dce5;
}

.dtp-pill.is-custom .dtp-trigger__divider {
  background: var(--color-primary-border);
}

.dtp-trigger__time {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-weight: 500;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dtp-trigger__chevron {
  flex-shrink: 0;
  color: #52525b;
  font-size: 0.78rem;
}

.dtp-pill__clear,
.dtp-pill__clear:focus,
.dtp-pill__clear:active {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 10px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.12);
  color: var(--color-primary-hover);
  cursor: pointer;
  transition: background-color .15s ease;
}

.dtp-pill__clear:hover {
  background: rgba(99, 102, 241, 0.22);
}

.dtp-pill__clear .pi {
  font-size: 0.6rem;
}

/* ------------------------------- panel -------------------------------- */
.dtp-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 1040;
  width: 456px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 160px);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 18px 18px 14px;
  background: var(--color-bg-canvas);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  box-shadow: 0 24px 48px -12px rgba(15, 23, 42, 0.22), 0 4px 12px rgba(15, 23, 42, 0.06);
  outline: none;
  text-align: left;
  cursor: default;
}

.dtp-panel__head {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.dtp-panel__glyph {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.dtp-panel__titles {
  min-width: 0;
}

.dtp-panel__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.25;
}

.dtp-panel__subtitle {
  margin: 2px 0 0;
  color: var(--color-text-muted);
  font-size: 0.74rem;
  line-height: 1.35;
}

/* ------------------------------ sections ------------------------------ */
.dtp-section {
  margin-top: 16px;
}

.dtp-section--time {
  padding-top: 14px;
  border-top: 1px solid var(--color-border);
}

.dtp-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 24px;
  margin-bottom: 8px;
}

.dtp-section__label {
  color: var(--color-text-muted);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.dtp-duration {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

/* ------------------------------- chips -------------------------------- */
/* The app resets every focused / pressed button's border and shadow with
   !important, so the chip's own outline is restated for those states. Active
   chips keep their weight, so picking one never shifts its neighbours. */
.dtp-chip,
.dtp-chip:focus,
.dtp-chip:active {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 11px;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-pill);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  font: inherit;
  font-size: 0.76rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.dtp-chip--sm,
.dtp-chip--sm:focus,
.dtp-chip--sm:active {
  height: 24px;
  padding: 0 10px;
  font-size: 0.7rem;
}

.dtp-chip:hover {
  background: var(--color-bg-soft);
  color: var(--color-text-primary);
}

.dtp-chip.is-active,
.dtp-chip.is-active:focus,
.dtp-chip.is-active:active {
  border-color: var(--color-primary-border) !important;
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.dtp-chip:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

.dtp-chip__live {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-success);
  animation: dtp-live 1.8s ease-out infinite;
}

@keyframes dtp-live {
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* -------------------------------- date -------------------------------- */
.dtp-date {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 40px;
  gap: 8px;
}

.dtp-date__step,
.dtp-date__step:focus,
.dtp-date__step:active {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0;
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.dtp-date__step:hover {
  border-color: var(--color-primary-border) !important;
  background: var(--color-primary-soft);
  color: var(--color-primary-hover);
}

.dtp-date__step:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

.dtp-date__step .pi {
  font-size: 0.72rem;
}

.dtp-date__field {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  height: 50px;
  padding: 0 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-canvas);
  transition: border-color .15s ease, box-shadow .15s ease;
}

.dtp-date__field:hover {
  border-color: var(--color-primary-border);
}

.dtp-date__field:focus-within,
.dtp-date__field.is-open {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-focus);
}

.dtp-date__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.dtp-date__primary,
.dtp-date__secondary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dtp-date__primary {
  color: var(--color-text-primary);
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.15;
}

.dtp-date__secondary {
  color: var(--color-text-muted);
  font-size: 0.7rem;
  line-height: 1.2;
}

.dtp-date__icon {
  flex-shrink: 0;
  color: var(--color-primary);
  font-size: 0.9rem;
}

/* The invisible control over the field. The app sizes, borders and rings
   every input and focused button with !important, so this restates its own. */
.dtp-date__control,
.dtp-date__control:focus,
.dtp-date__control:active {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100% !important;
  height: 100% !important;
  margin: 0;
  padding: 0 !important;
  border: 0 !important;
  border-radius: inherit !important;
  background: transparent;
  box-shadow: none !important;
  outline: none !important;
  cursor: pointer;
}

.dtp-date__native {
  opacity: 0;
  color: transparent;
  color-scheme: light;
}

.dtp-date__native::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: pointer;
}

/* ------------------------------ presets ------------------------------- */
.dtp-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* ------------------------------ timeline ------------------------------ */
/* The top padding is the room the handles' time bubbles open into. */
.dtp-timeline {
  margin-top: 6px;
  padding: 30px 11px 0;
}

.dtp-track {
  position: relative;
  height: 28px;
  cursor: pointer;
  touch-action: none;
  user-select: none;
}

/* Night, morning, noon, evening, night — and a faint mark every three hours. */
.dtp-track__rail {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 8px;
  transform: translateY(-50%);
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, #E0E7FF 0%, #E0E7FF 20%, #FEF3C7 32%, #FDE68A 50%, #FED7AA 68%, #E0E7FF 82%, #E0E7FF 100%);
}

.dtp-track__rail::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: repeating-linear-gradient(90deg, transparent 0 calc(12.5% - 1px), rgba(255, 255, 255, 0.95) calc(12.5% - 1px) 12.5%);
}

.dtp-track__band {
  position: absolute;
  top: 50%;
  height: 8px;
  transform: translateY(-50%);
  border-radius: var(--radius-pill);
  background: var(--color-primary);
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.14);
  cursor: grab;
}

.dtp-track__band:active {
  cursor: grabbing;
}

.dtp-track__now {
  position: absolute;
  top: 50%;
  width: 2px;
  height: 22px;
  transform: translate(-50%, -50%);
  border-radius: 2px;
  background: var(--color-success);
  pointer-events: none;
}

.dtp-track__now::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -4px;
  width: 7px;
  height: 7px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 0 2px #fff;
}

.dtp-handle,
.dtp-handle:focus,
.dtp-handle:active {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 22px;
  height: 22px;
  padding: 0;
  transform: translate(-50%, -50%);
  border: 2px solid var(--color-primary) !important;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2) !important;
  cursor: grab;
  transition: transform .12s ease, box-shadow .12s ease;
}

.dtp-handle:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.dtp-handle:focus-visible {
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25), 0 2px 6px rgba(15, 23, 42, 0.2) !important;
}

.dtp-handle.is-dragging {
  cursor: grabbing;
  transform: translate(-50%, -50%) scale(1.15);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35) !important;
}

.dtp-handle__bubble {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 9px);
  padding: 4px 8px;
  border-radius: 7px;
  background: var(--color-text-primary);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transform: translate(-50%, 4px);
  opacity: 0;
  pointer-events: none;
  transition: opacity .12s ease, transform .12s ease;
}

.dtp-handle__bubble::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: var(--color-text-primary);
}

.dtp-handle:hover .dtp-handle__bubble,
.dtp-handle:focus-visible .dtp-handle__bubble,
.dtp-handle.is-dragging .dtp-handle__bubble {
  opacity: 1;
  transform: translate(-50%, 0);
}

.dtp-ticks {
  position: relative;
  height: 16px;
  margin-top: 6px;
}

.dtp-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  color: var(--color-text-muted);
  font-size: 0.66rem;
  font-weight: 500;
  white-space: nowrap;
}

.dtp-tick:first-child {
  transform: translateX(-11px);
}

.dtp-tick:last-child {
  transform: translateX(calc(-100% + 11px));
}

/* ------------------------------- fields ------------------------------- */
.dtp-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: end;
  gap: 10px;
  margin-top: 14px;
}

.dtp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  margin: 0;
}

.dtp-field__label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-muted);
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.dtp-field__tag {
  padding: 1px 6px;
  border-radius: var(--radius-pill);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: 0.6rem;
  letter-spacing: 0;
  text-transform: none;
}

/* The app's input box (border, radius, focus ring) with a shorter height. */
.dtp-field__input {
  width: 100%;
  height: 44px !important;
  padding: 0 10px;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 0.95rem !important;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.dtp-field.is-invalid .dtp-field__input {
  border-color: var(--color-danger) !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
}

.dtp-fields__arrow {
  padding-bottom: 15px;
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.dtp-hint {
  margin: 8px 0 0;
  color: var(--color-text-muted);
  font-size: 0.7rem;
  line-height: 1.4;
}

.dtp-hint.is-error {
  color: var(--color-danger);
}

/* ------------------------------- footer ------------------------------- */
.dtp-panel__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--color-border);
}

.dtp-link,
.dtp-link:focus,
.dtp-link:active {
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-primary-hover);
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.dtp-link:hover {
  text-decoration: underline;
}

.dtp-panel__actions {
  display: flex;
  gap: 8px;
}

.dtp-btn {
  height: 36px;
  padding: 0 16px;
  border-radius: 10px;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.dtp-btn--ghost,
.dtp-btn--ghost:focus,
.dtp-btn--ghost:active {
  border: 1px solid var(--color-border) !important;
  background: var(--color-bg-canvas);
  color: var(--color-text-secondary);
}

.dtp-btn--ghost:hover {
  background: var(--color-bg-soft);
}

.dtp-btn--primary,
.dtp-btn--primary:focus,
.dtp-btn--primary:active {
  border: 1px solid var(--color-primary) !important;
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  box-shadow: 0 6px 14px -6px rgba(99, 102, 241, 0.6) !important;
}

.dtp-btn--primary:hover {
  border-color: var(--color-primary-hover) !important;
  background: var(--color-primary-hover);
}

.dtp-btn:focus-visible,
.dtp-link:focus-visible,
.dtp-pill__clear:focus-visible,
.dtp-trigger:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

/* ------------------------------ motion -------------------------------- */
.dtp-pop-enter-active,
.dtp-pop-leave-active {
  transition: opacity .16s ease, transform .16s ease;
  transform-origin: top right;
}

.dtp-pop-enter-from,
.dtp-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .dtp-pop-enter-active,
  .dtp-pop-leave-active,
  .dtp-handle,
  .dtp-handle__bubble {
    transition: none;
  }

  .dtp-chip__live {
    animation: none;
  }
}

/* Below the desktop layout the pill starts its row, so the panel opens from
   its left edge. */
@media (max-width: 991.98px) {
  .dtp-panel {
    right: auto;
    left: 0;
  }

  .dtp-pop-enter-active,
  .dtp-pop-leave-active {
    transform-origin: top left;
  }
}

/* A phone gives the panel the whole row the pill sits in. */
@media (max-width: 575.98px) {
  .dtp-panel {
    left: 0;
    right: 0;
    width: auto;
    max-width: none;
  }
}
</style>
